import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, X, UserCheck, Clock, Home, Phone, Mail, Car, Bike, ShieldCheck, AlertCircle } from "lucide-react";
import { parseVehicles } from "@/lib/vehicles";

export default function PendingResidentsManager({ onCountChange }) {
    const [pending, setPending] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState(null);

    const loadPending = async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/pending-residents");
            setPending(res.data);
            if (onCountChange) onCountChange(res.data.length);
        } catch (err) {
            toast.error("Could not load pending residents");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPending();
    }, []);

    const handleApprove = async (resident) => {
        setProcessingId(resident.user_id);
        try {
            await api.post(`/admin/residents/${resident.user_id}/approve`);
            toast.success(`Resident ${resident.name} for flat ${resident.tower}-${resident.flat_no} has been approved!`);
            loadPending();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not approve resident");
        } finally {
            setProcessingId(null);
        }
    };

    const handleReject = async (resident) => {
        const reason = window.prompt(
            `Enter reason for rejecting resident ${resident.name} (flat ${resident.tower}-${resident.flat_no}):`,
            "Information could not be verified by society office."
        );
        if (reason === null) return; // user cancelled

        setProcessingId(resident.user_id);
        try {
            await api.post(`/admin/residents/${resident.user_id}/reject`, { reason });
            toast.info(`Application for ${resident.name} rejected.`);
            loadPending();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not reject resident");
        } finally {
            setProcessingId(null);
        }
    };

    return (
        <div>
            <div className="pb-6">
                <h2 className="font-display text-2xl tracking-tight">Resident Approvals</h2>
                <p className="text-nest-ink2 text-sm mt-1">
                    Review and verify new resident registrations before enabling their society access.
                </p>
            </div>

            {loading ? (
                <div className="py-12 text-center text-nest-ink2">Loading pending profiles...</div>
            ) : pending.length === 0 ? (
                <div className="nest-card p-12 text-center text-nest-ink2">
                    <div className="h-12 w-12 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-600 mx-auto grid place-items-center mb-3">
                        <Check size={24} />
                    </div>
                    <p className="font-display text-lg text-nest-ink">All Clear!</p>
                    <p className="text-sm mt-1 max-w-md mx-auto">
                        There are no pending resident profiles awaiting review. When a resident registers via the web URL, their profile will appear here for verification.
                    </p>
                </div>
            ) : (
                <div className="grid gap-4">
                    {pending.map((r) => {
                        const vehicles = parseVehicles(r.vehicles);
                        const isProcessing = processingId === r.user_id;

                        return (
                            <div
                                key={r.user_id}
                                className="nest-card p-5 sm:p-6 flex flex-col md:flex-row md:items-center justify-between gap-5 border-l-4 border-l-amber-500 shadow-sm"
                                data-testid={`pending-resident-${r.user_id}`}
                            >
                                <div className="flex items-start gap-4">
                                    {r.picture ? (
                                        <img src={r.picture} alt="" className="h-14 w-14 rounded-full object-cover border border-nest-line flex-shrink-0" />
                                    ) : (
                                        <div className="h-14 w-14 rounded-2xl bg-amber-100 text-amber-900 grid place-items-center font-display font-semibold text-xl flex-shrink-0">
                                            {r.name?.[0] || "R"}
                                        </div>
                                    )}

                                    <div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="font-semibold text-lg text-nest-ink">{r.name}</h3>
                                            <span className="font-mono font-bold text-xs bg-nest-ink text-white px-2.5 py-0.5 rounded-md">
                                                Flat {r.tower}-{r.flat_no}
                                            </span>
                                            <span className={`text-[10px] uppercase font-bold tracking-wider px-2.5 py-0.5 rounded-full border ${
                                                r.occupancy === "tenant"
                                                    ? "bg-blue-50 text-blue-800 border-blue-200"
                                                    : "bg-emerald-50 text-emerald-800 border-emerald-200"
                                            }`}>
                                                {r.occupancy === "tenant" ? "Tenant" : "Owner"}
                                            </span>
                                            <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-300">
                                                Awaiting Approval
                                            </span>
                                        </div>

                                        <div className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-nest-ink2">
                                            <div className="flex items-center gap-1.5 truncate">
                                                <Mail size={13} className="text-nest-ink3 flex-shrink-0" />
                                                <span className="font-mono text-[11px] truncate">{r.email}</span>
                                            </div>
                                            {r.phone && (
                                                <div className="flex items-center gap-1.5">
                                                    <Phone size={13} className="text-nest-ink3 flex-shrink-0" />
                                                    <span>{r.phone}</span>
                                                </div>
                                            )}
                                        </div>

                                        {vehicles.length > 0 && (
                                            <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                                                <span className="text-[11px] text-nest-ink3 font-medium">Vehicles:</span>
                                                {vehicles.map((v, i) => (
                                                    <span key={i} className="inline-flex items-center gap-1 font-mono text-[11px] bg-nest-alt px-2 py-0.5 rounded border border-nest-line text-nest-ink">
                                                        {v.type === "bike" ? <Bike size={12} className="text-nest-clay" /> : <Car size={12} className="text-nest-clay" />}
                                                        {v.number}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex items-center gap-3 pt-3 md:pt-0 border-t md:border-t-0 border-nest-line justify-end">
                                    <button
                                        onClick={() => handleReject(r)}
                                        disabled={isProcessing}
                                        className="px-4 py-2.5 rounded-xl border border-nest-line text-nest-err hover:bg-red-50 text-sm font-semibold transition flex items-center gap-1.5 cursor-pointer"
                                        data-testid={`reject-resident-${r.user_id}`}
                                    >
                                        <X size={15} /> Reject
                                    </button>
                                    <button
                                        onClick={() => handleApprove(r)}
                                        disabled={isProcessing}
                                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition flex items-center gap-2 shadow-xs cursor-pointer active:scale-95"
                                        data-testid={`approve-resident-${r.user_id}`}
                                    >
                                        <Check size={16} /> Approve Resident
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
